import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import UpcomingDueCustomers from './UpcomingDueCustomers';
import InstallmentSummaryCards from './InstallmentSummaryCards';
import GreetingHeader from './GreetingHeader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Wallet, CreditCard, Receipt, TrendingUp, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CashierDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/dashboard/cashier-stats`, { withCredentials: true })
      .then(({ data }) => setStats(data))
      .catch(() => setStats({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-96 text-slate-400">Loading dashboard...</div>;

  const cards = [
    { title: 'Collected Today', value: `PKR ${(stats?.collected_today || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-500', testId: 'cashier-stat-today' },
    { title: 'Receipts Today', value: stats?.receipts_today || 0, icon: Receipt, color: 'text-blue-500', testId: 'cashier-stat-receipts-today' },
    { title: 'Collected By Me Today', value: `PKR ${(stats?.collected_by_me_today || 0).toLocaleString()}`, icon: User, color: 'text-amber-500', testId: 'cashier-stat-me-today' },
    { title: 'My Receipts Today', value: stats?.receipts_by_me_today || 0, icon: Receipt, color: 'text-amber-500', testId: 'cashier-stat-me-receipts-today' },
    { title: 'Collected This Month', value: `PKR ${(stats?.collected_this_month || 0).toLocaleString()}`, icon: TrendingUp, color: 'text-emerald-500', testId: 'cashier-stat-month' },
    { title: 'Cash Balance', value: `PKR ${(stats?.cash_balance || 0).toLocaleString()}`, icon: Wallet, color: 'text-emerald-500', testId: 'cashier-stat-cash' },
    { title: 'Bank Balance', value: `PKR ${(stats?.bank_balance || 0).toLocaleString()}`, icon: CreditCard, color: 'text-blue-500', testId: 'cashier-stat-bank' },
  ];

  return (
    <div className="space-y-6" data-testid="cashier-dashboard">
      <GreetingHeader
        testId="cashier-greeting"
        fallbackName="Cashier"
        subtitle="Cashier Dashboard · Track today's collections, receipts and cash/bank balances"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <Card key={i} className="bg-slate-900 border-slate-800 hover:-translate-y-1 transition-transform" data-testid={c.testId}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">{c.title}</CardTitle>
              <c.icon className={`h-4 w-4 ${c.color}`} />
            </CardHeader>
            <CardContent><div className="text-2xl font-bold text-white">{c.value}</div></CardContent>
          </Card>
        ))}
      </div>

      {stats?.by_method_today?.length > 0 && (
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader><CardTitle className="text-white">Today's Collections by Method</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800">
                  <TableHead className="text-slate-400">Method</TableHead>
                  <TableHead className="text-slate-400">Count</TableHead>
                  <TableHead className="text-slate-400">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.by_method_today.map((m, i) => (
                  <TableRow key={i} className="border-slate-800" data-testid="cashier-method-row">
                    <TableCell className="text-white">{m.method || 'Cash'}</TableCell>
                    <TableCell className="text-slate-300">{m.count}</TableCell>
                    <TableCell className="text-emerald-500">PKR {m.total?.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    <InstallmentSummaryCards />
      <UpcomingDueCustomers />

    </div>
  );
}
